import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { blankRow, rowError, overlapWarning, type PriceTableRowDraft } from "@/lib/price-table-rows";

const RATE_KEYS = ["rate1", "rate2", "rate3", "rate4", "rate5", "rate6", "rate7", "rate8", "rate9"] as const;

export interface PriceTableRatesEditorProps {
  value: PriceTableRowDraft[];
  onChange: (next: PriceTableRowDraft[]) => void;
  rateTypes: string[];
  disabled?: boolean;
}

export function PriceTableRatesEditor({ value, onChange, rateTypes, disabled }: PriceTableRatesEditorProps) {
  const patch = (i: number, p: Partial<PriceTableRowDraft>) => onChange(value.map((r, k) => (k === i ? { ...r, ...p } : r)));
  const addRow = () => onChange([...value, blankRow()]);
  const removeRow = (i: number) => onChange(value.filter((_, k) => k !== i));
  const numOrNull = (s: string): number | null => (s.trim() === "" ? null : Number(s));
  const warning = overlapWarning(value);

  return (
    <div className="space-y-2">
      <div className="overflow-x-auto">
        <table className="text-sm border-collapse">
          <thead>
            <tr>
              <th className="border px-2 py-1 text-left">Rate type</th>
              <th className="border px-2 py-1">From</th>
              <th className="border px-2 py-1">To</th>
              {RATE_KEYS.map((_, i) => <th key={i} className="border px-2 py-1">R{i + 1}</th>)}
              <th className="border px-2 py-1 text-left">Range desc</th>
              <th className="border px-2 py-1" />
            </tr>
          </thead>
          <tbody>
            {value.map((row, i) => {
              const err = rowError(row);
              return (
                <tr key={i}>
                  <td className="border px-1">
                    <select aria-label={`Rate type, row ${i + 1}`} className="w-28 bg-transparent" value={row.rateType ?? ""} disabled={disabled}
                      onChange={(e) => patch(i, { rateType: e.target.value || null })}>
                      <option value="">—</option>
                      {rateTypes.map((rt) => <option key={rt} value={rt}>{rt}</option>)}
                    </select>
                  </td>
                  <td className="border px-1"><Input aria-label={`From, row ${i + 1}`} className="w-16" type="number" value={row.rangeFrom} disabled={disabled} onChange={(e) => patch(i, { rangeFrom: Number(e.target.value) })} /></td>
                  <td className="border px-1"><Input aria-label={`To, row ${i + 1}`} className="w-16" type="number" value={row.rangeTo} disabled={disabled} onChange={(e) => patch(i, { rangeTo: Number(e.target.value) })} /></td>
                  {RATE_KEYS.map((k, ri) => (
                    <td key={k} className="border px-1">
                      <Input aria-label={`Rate ${ri + 1}, row ${i + 1}`} className="w-20" inputMode="decimal" step="0.0001" type="number"
                        value={row[k] ?? ""} disabled={disabled} onChange={(e) => patch(i, { [k]: numOrNull(e.target.value) })} />
                    </td>
                  ))}
                  <td className="border px-1"><Input aria-label={`Range desc, row ${i + 1}`} className="w-28" value={row.rangeDesc ?? ""} disabled={disabled} onChange={(e) => patch(i, { rangeDesc: e.target.value || null })} /></td>
                  <td className="border px-1 text-center">
                    {!disabled && <button type="button" aria-label={`Remove row ${i + 1}`} onClick={() => removeRow(i)} className="text-destructive">✕</button>}
                    {err && <div className="text-xs text-destructive whitespace-nowrap">{err}</div>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {warning && <p className="text-xs text-amber-600">{warning}</p>}
      {!disabled && <Button type="button" variant="outline" size="sm" onClick={addRow}>+ Add row</Button>}
    </div>
  );
}
